import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CmAuthService, CmTimeoutService } from '@ats/cm/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, map, switchMap, take, tap } from 'rxjs';
import { CmTimeoutModalComponent } from './cm-timeout-modal/cm-timeout-modal.component';

@UntilDestroy()
@Component({
    selector: 'cm-timeout',
    templateUrl: './cm-timeout.component.html',
    styleUrls: ['./cm-timeout.component.scss'],
    standalone: false
})
export class CmTimeoutComponent implements OnInit {
	private dialogRef: MatDialogRef<CmTimeoutModalComponent>;
	private dialogSub: Subscription;
	private logoutSub: Subscription;

	constructor(
		private readonly auth: CmAuthService,
		private readonly dialog: MatDialog,
		private readonly timeoutService: CmTimeoutService
	) {}

	ngOnInit(): void {
		this.timeoutService.expirationTime$.pipe(untilDestroyed(this)).subscribe(() => {
			this.stopTimeout();
		});

		this.auth.token$.pipe(untilDestroyed(this)).subscribe(token => {
			if (token) {
				this.timeoutService.setTimeoutToStorage(token);
				this.startTimeout();
			} else {
				this.stopTimeout();
				this.timeoutService.deleteExpiration();
			}
		});
	}

	private scheduleLogout(): void {
		// This prevents any dangling logout subscriptions
		if (this.logoutSub) {
			this.logoutSub.unsubscribe();
		}
		this.logoutSub = this.timeoutService.warnDuration$.subscribe(() => {
			this.signOutActions();
		});
	}

	private stopTimeout(): void {
		if (this.dialogRef) {
			this.dialogRef.close(null);
		}
		if (this.logoutSub) {
			this.logoutSub.unsubscribe();
		}
	}

	private startTimeout(): void {
		if (this.dialogSub) {
			this.dialogSub.unsubscribe();
		}
		const validateAuth$ = this.auth.validateToken$().pipe(take(1));
		const warnDialogRef$ = this.timeoutService.atWarnTime$.pipe(
			map(() => {
				this.dialogRef = this.dialog.open(CmTimeoutModalComponent, {
					maxWidth: '480px',
					width: '80%',
					disableClose: true,
				});
				return this.dialogRef;
			}),
			tap(() => this.scheduleLogout()),
			switchMap(dialogRef => dialogRef.afterClosed())
		);

		this.dialogSub = warnDialogRef$.subscribe(closeResult => {
			if (closeResult) {
				validateAuth$.subscribe(() => {
					this.timeoutService.resetExpiration();
				});
			} else if (closeResult === false) {
				this.signOutActions();
			}
		});
	}

	private signOutActions(): void {
		this.stopTimeout();
		this.auth.logout$();
		this.timeoutService.deleteExpiration();
	}

	/** Diagnostic code BEGIN **/
	/* Enable displaying the remaining time in the template
	- You will also have to use ChangeDetectorRef.detectChanges() to display this in the template
	- To cause faster timeouts you can directly change the timer value in the timeout service

	this.remainingTime$ = this.timeoutService.remainingTime$;

		private getRemainingTime() {
		const intervalWithReset$ = this.expirationTime$.pipe(switchMap(() => interval(1000)));
		return combineLatest([this.expirationTime$, intervalWithReset$]).pipe(
			map(([warnTimeMS, intervalS]) => {
				return warnTimeMS - intervalS * 1000;
			})
		);
	}

	*/
	/** Diagnostic code END **/
}
